import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { X, Download, Smartphone } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const getGlobalPrompt = (): BeforeInstallPromptEvent | null => (window as any).__pwaInstallPrompt;
const setGlobalPrompt = (val: BeforeInstallPromptEvent | null) => { (window as any).__pwaInstallPrompt = val; };

const getInstallContext = () => {
  const hostname = window.location.hostname;
  const userAgent = navigator.userAgent;

  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  // IMPORTANTE: não bloquear `lovable.app` em geral, pois inclui a URL
  // publicada (ai-bicho-oracle.lovable.app). Só bloqueamos o preview real
  // (`id-preview--*.lovable.app`) e o domínio de sandbox `lovableproject.com`.
  return {
    isInIframe,
    isPreviewHost:
      hostname.includes('id-preview--') ||
      hostname.includes('lovableproject.com'),
    isIOS: /iPhone|iPad|iPod/i.test(userAgent),
    isChromeLike: /Chrome|CriOS|Edg|OPR|SamsungBrowser/i.test(userAgent),
  };
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    // Detect if already installed
    const installed = window.matchMedia('(display-mode: standalone)').matches ||
                     (window.navigator as any).standalone === true;

    if (installed) {
      setIsInstalled(true);
      setShowPrompt(false);
      return;
    }

    // Respect dismiss cooldown
    const declinedTime = localStorage.getItem('pwa-install-declined-time');
    if (declinedTime) {
      const daysSinceDeclined = (Date.now() - parseInt(declinedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDeclined < 7) {
        setShowPrompt(false);
        return;
      }
      localStorage.removeItem('pwa-install-declined');
      localStorage.removeItem('pwa-install-declined-time');
    }

    // Check if global prompt was already captured by main.tsx
    const globalPrompt = getGlobalPrompt();
    if (globalPrompt) {
      setDeferredPrompt(globalPrompt);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setGlobalPrompt(promptEvent);
      setDeferredPrompt(promptEvent);
      // Garantir exibição do banner mesmo quando o evento chega tardiamente
      // (após o tryShow inicial). Respeita iframe/preview e cooldown já tratados acima.
      const ctx = getInstallContext();
      if (!ctx.isInIframe && !ctx.isPreviewHost) {
        setTimeout(() => setShowPrompt(true), 2500);
      }
    };

    const handleAppInstalled = () => {
      setShowPrompt(false);
      setIsInstalled(true);
      setDeferredPrompt(null);
      setGlobalPrompt(null);
      localStorage.removeItem('pwa-install-declined');
      localStorage.removeItem('pwa-install-declined-time');
      toast({
        title: "✅ App instalado com sucesso!",
        description: "Procure o ícone 'AI Palpite Guru' na sua tela inicial.",
      });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const { isIOS, isInIframe, isPreviewHost } = getInstallContext();

    // Só exibir o banner quando houver prompt nativo disponível (Android/Chrome)
    // ou em iOS (instruções manuais). Evita banner permanente e reduz overlays
    // que podem causar crash de renderização ("Ah, não!") em mobile.
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    if (!isInIframe && !isPreviewHost) {
      const tryShow = () => {
        if (getGlobalPrompt() || isIOS) {
          showTimer = setTimeout(() => setShowPrompt(true), 2500);
          return true;
        }
        return false;
      };
      tryShow();
    }

    // Polling reduzido: 3 tentativas (3s, 6s, 9s) — só ativa o banner se o prompt aparecer
    // Polling estendido: tenta por ~60s (20 tentativas a cada 3s).
    // Chrome só dispara `beforeinstallprompt` após critério de engajamento
    // do usuário (~30s de interação). Antes parávamos em 9s e perdíamos o evento.
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      pollCount++;
      const gp = getGlobalPrompt();
      if (gp && !deferredPrompt) {
        setDeferredPrompt(gp);
        const ctx = getInstallContext();
        if (!ctx.isInIframe && !ctx.isPreviewHost) {
          setShowPrompt(true);
        }
        clearInterval(pollInterval);
      }
      if (pollCount >= 20) clearInterval(pollInterval);
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearInterval(pollInterval);
      clearTimeout(showTimer);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    const prompt = deferredPrompt || getGlobalPrompt();

    if (!prompt) {
      const { isInIframe, isPreviewHost, isIOS, isChromeLike } = getInstallContext();

      console.info('PWA prompt indisponível no momento', {
        isInIframe,
        isPreviewHost,
        isIOS,
        isChromeLike,
      });

      if (isInIframe || isPreviewHost) {
        toast({
          title: "⚠️ Instalação indisponível no preview",
          description: "No preview do Lovable o prompt nativo não é liberado. Teste no link publicado no Chrome.",
        });
        return;
      }

      if (isIOS) {
        toast({
          title: "📲 Instalação manual no iPhone/iPad",
          description: "No Safari, toque em Compartilhar e depois em Adicionar à Tela de Início.",
        });
        return;
      }

      if (isChromeLike) {
        toast({
          title: "⏳ Instalação ainda não liberada",
          description: "O Chrome ainda não disponibilizou o prompt nativo nesta página. Tente novamente em alguns segundos ou use o menu ⋮ > Instalar app.",
        });
        return;
      }

      toast({
        title: "⚠️ Instalação não disponível",
        description: "Este navegador ainda não liberou o prompt nativo de instalação nesta página.",
      });
      return;
    }

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        localStorage.removeItem('pwa-install-declined');
        localStorage.removeItem('pwa-install-declined-time');
        toast({
          title: "📲 Instalando...",
          description: "O app está sendo instalado. Procure o ícone na sua tela inicial.",
        });
      }
    } catch (err) {
      console.warn('Erro ao chamar prompt nativo:', err);
    }

    setDeferredPrompt(null);
    setGlobalPrompt(null);
    setShowPrompt(false);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-declined', 'true');
    localStorage.setItem('pwa-install-declined-time', Date.now().toString());
    setShowPrompt(false);
  };

  // Hide only when installed or explicitly dismissed during cooldown
  if (!showPrompt || isInstalled) return null;

  return (
    <div className="fixed left-4 right-4 md:left-auto md:right-4 md:w-96 z-[60] animate-in slide-in-from-bottom-5 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-4">
      <Card className="bg-gradient-to-r from-primary to-blue-600 border-none shadow-2xl">
        <div className="p-4 text-primary-foreground">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {isMobile ? <Smartphone className="h-5 w-5" /> : <Download className="h-5 w-5" />}
              <h3 className="font-bold text-lg text-white">Instalar AI Palpite Guru</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-primary-foreground hover:bg-white/20 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <p className="text-sm mb-4 opacity-90 text-white">
            Toque no botão abaixo para instalar o app direto na sua tela inicial!
          </p>

          <div className="flex gap-2">
            <Button
              onClick={handleInstall}
              className="flex-1 bg-background text-foreground hover:bg-background/90 font-semibold"
            >
              Instalar Agora
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="text-primary-foreground hover:bg-white/20"
            >
              Depois
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InstallPrompt;
